import React, { useEffect, useState } from "react";
import {
    Button,
    Dropdown,
    Icon,
    Label,
    Loader,
    Message,
    Pagination,
    Segment,
    Table,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import {
    downloadReportArtifact,
    exportSalesOrderReport,
    getSalesOrderReport,
} from "../../../services/reporting.service";
import {
    ReportFilters,
    SalesReportResponse,
    SortDirection,
} from "../../../types/models/Reporting";
import {
    formatCurrency,
    formatNumber,
    formatPercent,
    formatReportDate,
} from "../reportingFormatters";
import SalesTrendModal from "../modals/SalesTrendModal";
import { PROSPECT_ORIGIN_OPTIONS } from "../../../utils/dropdownOptions";

interface Props {
    filters: ReportFilters;
    reloadToken: number;
}

const originOptions = [
    ...PROSPECT_ORIGIN_OPTIONS,
    { key: "UNKNOWN", value: "UNKNOWN", text: "Sin origen" },
];

const originLabel = (origin: string) =>
    originOptions.find((item) => item.value === origin)?.text || origin;

const SalesReport = ({ filters, reloadToken }: Props) => {
    const [data, setData] = useState<SalesReportResponse>();
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [origin, setOrigin] = useState<string>();
    const [sortProperty, setSortProperty] = useState("completedAt");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [trendOpen, setTrendOpen] = useState(false);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const response = await getSalesOrderReport(filters, {
                    page,
                    size,
                    sort: `${sortProperty},${sortDirection}`,
                }, origin);
                if (mounted) setData(response.data);
            } catch (error: any) {
                if (mounted) toast.error(error.message || "No fue posible consultar el reporte de ventas.");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [filters, reloadToken, page, size, origin, sortProperty, sortDirection]);

    useEffect(() => setPage(0), [filters, reloadToken, origin]);

    const changeSort = (property: string) => {
        setPage(0);
        setSortDirection((current) => sortProperty === property && current === "asc" ? "desc" : "asc");
        setSortProperty(property);
    };
    const sorted = (property: string) => sortProperty === property
        ? (sortDirection === "asc" ? "ascending" : "descending")
        : undefined;

    const exportExcel = async () => {
        setExporting(true);
        try {
            const response = await exportSalesOrderReport(
                filters,
                `${sortProperty},${sortDirection}`,
                origin
            );
            await downloadReportArtifact(response.data);
            toast.success(`Reporte generado: ${response.data.fileName}`);
        } catch (error: any) {
            toast.error(error.message || "No fue posible generar el reporte de ventas.");
        } finally {
            setExporting(false);
        }
    };

    const summary = data?.summary;

    return (
        <div className="dashboard-section">
            <div className="dashboard-section-heading">
                <div>
                    <span className="dashboard-eyebrow">Reporte de ventas</span>
                    <h2>Proyectos terminados y cobranza realizada</h2>
                    <p>Concentra ventas consolidadas, saldos pendientes, origen y tiempo de ejecución.</p>
                </div>
                <div className="dashboard-table-filters">
                    <Dropdown
                        clearable
                        selection
                        placeholder="Todos los orígenes"
                        options={originOptions}
                        value={origin || ""}
                        onChange={(_, value) => setOrigin(String(value.value || "") || undefined)}
                    />
                    <Button basic icon labelPosition="left" onClick={() => setTrendOpen(true)}>
                        <Icon name="chart line" /> Tendencia de ventas
                    </Button>
                    <Button secondary icon labelPosition="left" onClick={exportExcel} loading={exporting} disabled={exporting}>
                        <Icon name="file excel outline" /> Exportar ventas
                    </Button>
                </div>
            </div>

            {data && (
                <Message
                    size="mini"
                    info
                    content="El rango de fechas se aplica a la terminación del proyecto, no a la creación del pedido. Así se incluyen cierres del periodo provenientes de pedidos anteriores."
                />
            )}

            {summary && (
                <div className="report-summary-grid">
                    <div><span>Proyectos terminados</span><strong>{formatNumber(summary.completedProjects)}</strong></div>
                    <div><span>Venta total</span><strong>{formatCurrency(summary.totalSalesAmount)}</strong></div>
                    <div><span>Cobrado</span><strong>{formatCurrency(summary.paidAmount)}</strong></div>
                    <div><span>Saldo pendiente</span><strong>{formatCurrency(summary.outstandingBalance)}</strong></div>
                    <div><span>Cobranza</span><strong>{formatPercent(summary.collectionRatePercent)}</strong></div>
                    <div><span>Ticket promedio</span><strong>{formatCurrency(summary.averageTicket)}</strong></div>
                    <div><span>Liquidados</span><strong>{formatNumber(summary.fullyPaidProjects)}</strong></div>
                    <div><span>Con saldo</span><strong>{formatNumber(summary.projectsWithBalance)}</strong></div>
                    <div><span>Anticipos</span><strong>{formatCurrency(summary.advanceAmount)}</strong></div>
                    <div><span>Abonos</span><strong>{formatCurrency(summary.installmentAmount)}</strong></div>
                    <div><span>Parciales</span><strong>{formatCurrency(summary.partialSettlementAmount)}</strong></div>
                    <div><span>Liquidaciones</span><strong>{formatCurrency(summary.fullSettlementAmount)}</strong></div>
                </div>
            )}

            <Segment>
                {loading && <Loader active inline="centered">Consultando réplica...</Loader>}
                {!loading && data?.orders.length === 0 && <Message info content="No existen proyectos terminados para los filtros seleccionados." />}
                {!loading && Boolean(data?.orders.length) && (
                    <div className="dashboard-table-scroll">
                        <Table sortable striped compact selectable className="dashboard-table dashboard-report-table">
                            <Table.Header><Table.Row>
                                <Table.HeaderCell sorted={sorted("invoice")} onClick={() => changeSort("invoice")}>Factura</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("completedAt")} onClick={() => changeSort("completedAt")}>Terminación</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("daysToComplete")} onClick={() => changeSort("daysToComplete")} textAlign="right">Días</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("clientName")} onClick={() => changeSort("clientName")}>Cliente</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("productName")} onClick={() => changeSort("productName")}>Producto</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("advisorName")} onClick={() => changeSort("advisorName")}>Asesor</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("origin")} onClick={() => changeSort("origin")}>Origen</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("totalAmount")} onClick={() => changeSort("totalAmount")} textAlign="right">Venta</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("paidAmount")} onClick={() => changeSort("paidAmount")} textAlign="right">Cobrado</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("outstandingBalance")} onClick={() => changeSort("outstandingBalance")} textAlign="right">Saldo</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("collectionRatePercent")} onClick={() => changeSort("collectionRatePercent")} textAlign="right">Cobranza</Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row></Table.Header>
                            <Table.Body>{data?.orders.map((order) => (
                                <Table.Row key={order.orderId} warning={order.outstandingBalance > 0}>
                                    <Table.Cell><strong>#{order.invoice || "—"}</strong></Table.Cell>
                                    <Table.Cell>{formatReportDate(order.completedAt)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatNumber(order.daysToComplete)}</Table.Cell>
                                    <Table.Cell><strong>{order.clientFullName || "Sin cliente"}</strong><small>{order.phone || "Sin teléfono"}</small></Table.Cell>
                                    <Table.Cell><strong>{order.productName}</strong><small>{order.productSku}</small></Table.Cell>
                                    <Table.Cell>{order.advisorName || order.advisorUsername}</Table.Cell>
                                    <Table.Cell><Label basic>{originLabel(order.origin)}</Label></Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(order.totalAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(order.paidAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right"><strong>{formatCurrency(order.outstandingBalance)}</strong></Table.Cell>
                                    <Table.Cell textAlign="right">{formatPercent(order.collectionRatePercent)}</Table.Cell>
                                    <Table.Cell collapsing>
                                        <Button
                                            as="a"
                                            secondary
                                            icon="search"
                                            size="mini"
                                            href={`/orders/${order.orderId}/details`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            ))}</Table.Body>
                        </Table>
                    </div>
                )}
                <div className="dashboard-pagination dashboard-pagination--split">
                    <span>{formatNumber(data?.totalElements)} proyectos terminados</span>
                    <Pagination
                        activePage={page + 1}
                        totalPages={Math.max(data?.totalPages || 1, 1)}
                        onPageChange={(_, pagination) => setPage(Number(pagination.activePage) - 1)}
                    />
                    <Dropdown
                        compact
                        selection
                        value={size}
                        options={[10, 25, 50, 100].map((value) => ({ key: value, value, text: `${value} filas` }))}
                        onChange={(_, value) => { setPage(0); setSize(Number(value.value)); }}
                    />
                </div>
            </Segment>
            <SalesTrendModal
                open={trendOpen}
                filters={filters}
                origin={origin}
                onClose={() => setTrendOpen(false)}
            />
        </div>
    );
};

export default SalesReport;
